import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOptimizedPreviewMode } from '@/hooks/useOptimizedPreviewMode';
import { OptimizedPreviewProjectList } from '@/components/preview/OptimizedPreviewProjectList';
import Header from '@/components/layout/Header';
import ProjectList from '@/components/projects/ProjectList';
import { Skeleton } from '@/components/ui/skeleton';
import { logger } from '@/lib/logger';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';

const PublicPreview = () => {
  const isOptimizedModeEnabled = useGlobalFeatureFlag('ENABLE_OPTIMIZED_PREVIEW_MODE');
  const isOptimizedComponentsEnabled = useGlobalFeatureFlag('ENABLE_PREVIEW_COMPONENTS');

  // Legacy preview mode setup
  const { enablePreviewMode, isPreviewMode, loading } = useAuth();
  
  // Optimized preview mode setup
  const optimizedPreview = useOptimizedPreviewMode();

  const isReady = isOptimizedModeEnabled ? optimizedPreview.isReady : isPreviewMode;
  const isLoading = isOptimizedModeEnabled ? optimizedPreview.isLoading : loading;

  // Legacy setup effect (used when optimization disabled)
  useEffect(() => {
    if (isOptimizedModeEnabled) return;

    const setupPreview = async () => {
      try {
        logger.debug('🎬 Setting up public preview mode');
        await enablePreviewMode();
      } catch (error) {
        logger.error('❌ Failed to setup preview mode:', error);
      }
    };

    if (!isPreviewMode && !loading) {
      setupPreview();
    }
  }, [enablePreviewMode, isPreviewMode, loading, isOptimizedModeEnabled]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b">
          <div className="container py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </div>
        <main className="container py-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="rounded-lg border p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <h2 className="font-semibold text-primary mb-2">🎬 Preview Mode</h2>
          <p className="text-sm text-muted-foreground">
          Bạn đang xem dữ liệu thật trong chế độ preview chỉ đọc. Để truy cập đầy đủ, vui lòng đăng nhập.
        </p>
      </div>
      {isOptimizedComponentsEnabled ? <OptimizedPreviewProjectList /> : <ProjectList />}
    </main>
  </div>
);
};

export default PublicPreview;